import {
  PROVIDER_IDS,
  PROVIDER_LABEL,
  type MeResponse,
  type ProviderCapabilities,
  type ProviderId,
  type ProviderStatus,
} from '@profiler/shared'
import type { EnrichField } from './api'

/**
 * What the browser is allowed to know about the two providers.
 *
 * There is deliberately no hard-coded capability table here. Capabilities are a
 * property of the adapter that implements the seam, the API reports what each
 * adapter declares, and a second copy in the browser would be a copy that can
 * disagree — which is how a button ends up offered for something the active
 * provider cannot do. The only static text below is prose about billing, which
 * nothing is enabled or disabled on.
 */

/** One line of orientation per provider. Never used to enable or disable anything. */
export const PROVIDER_BLURB: Record<ProviderId, string> = {
  enrichlayer:
    'The provider this tool was built on. Person Search bills per profile returned, so a search that finds nobody is free, while company and role lookups are charged even when they find nothing.',
  enrichmentapi:
    'Charged only on a 200 response, so every miss is free. Candidates are discovered cheaply with Employee Finder and only the most promising are bought as full profiles, because Employee Finder alone returns a composed "Title at Company" string and cannot satisfy the admission gates.',
  apollo:
    'An enrichment source only: it does not find people here. A match returns the work email in the response and is charged as a reveal, so there is no free lookup to lead with. Phone numbers are revealed asynchronously at eight credits each and arrive by callback, usually within a few minutes; one that does not arrive is collected by the housekeeping sweep rather than written off. This plan exposes no credit balance, so none is shown.',
}

interface CapabilityCopy {
  /** Shown when the provider has it. */
  can: string
  /** Shown when it does not. This is the sentence that explains a disabled button. */
  cannot: string
}

/**
 * British English, and phrased as a consequence rather than a field name: the
 * reader of this list is deciding whether a switch will break their workflow.
 */
export const CAPABILITY_COPY: Record<keyof ProviderCapabilities, CapabilityCopy> = {
  freeOnMiss: {
    can: 'A search that finds nobody is free',
    cannot: 'A search that finds nobody is still charged',
  },
  booleanTitleSearch: {
    can: 'Filters titles server-side, so only plausible people are bought',
    cannot: 'No title search: candidates are filtered here, after they are listed',
  },
  asyncWorkEmail: {
    can: 'Work email arrives a few minutes later, by webhook',
    cannot: 'Work email answers immediately, in the response',
  },
  personalEmail: { can: 'Personal email addresses', cannot: 'No personal email addresses' },
  personalPhone: { can: 'Personal phone numbers', cannot: 'No personal phone numbers' },
  companyLookup: {
    can: 'Resolves a company from a domain or name',
    cannot: 'No company lookup: a company must be identified before people can be found',
  },
  creditBalance: { can: 'Reports its remaining balance', cannot: 'Does not report a balance' },
  discovery: {
    can: 'Finds people: can be the active provider for a job',
    cannot: 'Does not find people, so it cannot be the active provider',
  },
  enrichment: {
    can: 'Can be chosen as a source when enriching selected contacts',
    cannot: 'Cannot enrich contacts',
  },
  asyncPhone: {
    can: 'Phone numbers arrive a little later, by webhook',
    cannot: 'Phone numbers answer immediately, in the response',
  },
  /*
   * Prices, not capabilities. They live on the same object because the estimate
   * and the ladder must read one figure rather than two, but there is nothing
   * to say about them in a list of what a source can and cannot do — the price
   * is shown where it is spent, next to the button.
   */
  workEmailFreeOnMiss: {
    can: 'A work email lookup that finds nothing costs nothing',
    cannot: 'A work email lookup is charged whether or not it finds one',
  },
  // Prices rather than capabilities: they are quoted beside the buttons that
  // spend them, and a row in this list saying "1" would explain nothing.
  workEmailCredits: { can: '', cannot: '' },
  personalEmailCredits: { can: '', cannot: '' },
  phoneCredits: { can: '', cannot: '' },
}

/**
 * Capabilities whose absent form is a statement of fact, not a missing feature.
 *
 * `asyncWorkEmail: false` is not a shortcoming — it means the address is in the
 * response instead — and marking it as a loss would make the better of the two
 * behaviours look like the worse one.
 */
const NEUTRAL: ReadonlySet<keyof ProviderCapabilities> = new Set([
  'asyncWorkEmail',
  // Same shape of fact: answering immediately is not worse than answering later.
  'asyncPhone',
  // These two describe a ROLE, not a shortfall. Apollo not finding people is
  // what it is for, and painting it red would read as a source that is broken.
  'discovery',
  'enrichment',
])

export interface CapabilityLine {
  key: keyof ProviderCapabilities
  text: string
  supported: boolean
  /** False for capabilities where the absent form is not a loss. */
  isLoss: boolean
}

export function capabilityLines(capabilities: ProviderCapabilities): CapabilityLine[] {
  return (
    (Object.keys(CAPABILITY_COPY) as Array<keyof ProviderCapabilities>)
      // A price is not a capability. It is in the shape because the backend reports
      // it there; it has no sentence, and rendering one would print "false".
      .filter((key) => typeof capabilities[key] === 'boolean')
      .map((key) => {
        const supported = capabilities[key] as boolean
        return {
          key,
          text: supported ? CAPABILITY_COPY[key].can : CAPABILITY_COPY[key].cannot,
          supported,
          isLoss: !supported && !NEUTRAL.has(key),
        }
      })
  )
}

/**
 * Provider status as this screen should read it.
 *
 * `reported` is the honest third state: not "Enrich Layer" and not an error, but
 * an API that has not been asked this question yet.
 */
export interface ProviderView {
  reported: boolean
  activeId: ProviderId | null
  active: ProviderStatus | null
  all: ProviderStatus[]
  /** Null when unknown. Callers must not read a null as "everything is allowed". */
  capabilities: ProviderCapabilities | null
}

export function providerView(me: MeResponse | undefined): ProviderView {
  const all = me?.providers ?? []
  const activeId = me?.activeProvider ?? null
  // Trust the flag on the row over the top-level id only when the id is absent:
  // one of them is the answer to "which is active" and disagreement between them
  // is a backend bug, not something to paper over.
  const active = all.find((p) => (activeId ? p.id === activeId : p.active)) ?? null
  return {
    reported: all.length > 0,
    activeId: activeId ?? active?.id ?? null,
    active,
    all,
    capabilities: active?.capabilities ?? null,
  }
}

/** Both providers, in a stable order, with a placeholder for one the API omits. */
export function orderedProviders(
  view: ProviderView,
): Array<{ id: ProviderId; label: string; status: ProviderStatus | null }> {
  return PROVIDER_IDS.map((id) => ({
    id,
    label: view.all.find((p) => p.id === id)?.label ?? PROVIDER_LABEL[id],
    status: view.all.find((p) => p.id === id) ?? null,
  }))
}

/** Which capability each enrichment field needs. Work email is on both providers. */
const FIELD_REQUIRES: Record<EnrichField, keyof ProviderCapabilities | null> = {
  work_email: null,
  personal_email: 'personalEmail',
  phone: 'personalPhone',
}

/**
 * Credits per contact per field, per provider.
 *
 * Per provider because the same button spends a different amount on each: Enrich
 * Layer's Work Email Lookup is 3 and is charged on any response, hit or miss,
 * which is why the API reserves it against the monthly cap before queueing;
 * EnrichmentAPI's Email Finder is 2 and charges only on a 200.
 *
 * `null` where a provider sells no such thing, and deliberately not 0. A zero
 * renders as "free" above the one button in the product that spends credits per
 * click, and it is reachable: the contract lets the API report `activeProvider`
 * without `providers`, in which case capabilities are unknown, the field stays
 * enabled by design, and a 0 here would price it at nothing. Unknown is the
 * honest word for a price nobody has published.
 *
 * These are the documented figures; calibration in Settings is what confirms
 * them against the real bill.
 */
export const FIELD_COST: Record<ProviderId, Record<EnrichField, number | null>> = {
  enrichlayer: { work_email: 3, personal_email: 1, phone: 1 },
  enrichmentapi: { work_email: 2, personal_email: null, phone: null },
  /*
   * Apollo prices a REVEAL rather than a lookup, and what one costs depends on
   * the plan. These are the floor; the figure people should trust is whatever
   * calibration measures against the real account, which is what Settings is
   * for. Quoted rather than left null because a source offered with no price at
   * all reads as free.
   */
  /*
   * Measured against the live account rather than published: an email reveal is
   * one credit and a PHONE reveal is eight, which Apollo reports as
   * `credits_consumed` when it delivers. Eight is the most expensive thing this
   * product can be asked to buy per contact, and the button that spends it says
   * so before it is pressed.
   */
  apollo: { work_email: 1, personal_email: 1, phone: 8 },
}

/**
 * The price list to quote from.
 *
 * Falls back to Enrich Layer's figures only while the API reports no provider at
 * all, which is the single-provider deployment those figures came from.
 */
export function fieldCosts(
  view: ProviderView,
  providerId?: ProviderId | null,
): Record<EnrichField, number | null> {
  return FIELD_COST[providerId ?? view.activeId ?? 'enrichlayer']
}

/**
 * The sources that can enrich a selection, and whether each is usable right now.
 *
 * Which source to buy from is a per-request choice because the cost is per
 * contact — so this list is what the Enrich dialog offers, and it is built from
 * what the API reports rather than from a table here. A source with no key is
 * listed and disabled rather than hidden: "Apollo is not set up" is a useful
 * thing to learn, and a silently missing option is not.
 */
export interface EnrichmentSource {
  id: ProviderId
  label: string
  status: ProviderStatus | null
  capabilities: ProviderCapabilities | null
  /** Null when it can be used; otherwise why not, in words. */
  unavailable: string | null
}

export function enrichmentSources(view: ProviderView): EnrichmentSource[] {
  return view.all
    .filter((p) => p.capabilities.enrichment)
    .map((p) => ({
      id: p.id,
      label: p.label,
      status: p,
      capabilities: p.capabilities,
      unavailable: p.keyConfigured ? null : `${p.label} has no API key configured yet.`,
    }))
}

/**
 * Why this field cannot be bought right now, or null when it can.
 *
 * Unknown capabilities leave the field enabled. The alternative — disabling on a
 * guess — would take away work the current provider does support, and the
 * absence of the field means the API has not been upgraded yet rather than that
 * the provider has lost a feature.
 */
export function fieldUnavailableReason(
  field: EnrichField,
  view: ProviderView,
  providerId?: ProviderId | null,
): string | null {
  const needs = FIELD_REQUIRES[field]
  if (needs === null) return null

  const chosen = providerId ? (view.all.find((p) => p.id === providerId) ?? null) : null
  const capabilities = chosen?.capabilities ?? (providerId ? null : view.capabilities)
  if (!view.reported || !capabilities) return null
  if (capabilities[needs]) return null

  const label =
    chosen?.label ??
    view.active?.label ??
    ((providerId ?? view.activeId)
      ? PROVIDER_LABEL[(providerId ?? view.activeId) as ProviderId]
      : 'This source')
  return `${label} has no endpoint for this. Choose another source above.`
}

const FIELD_PHRASE: Record<EnrichField, string> = {
  work_email: 'a work email',
  personal_email: 'a personal email',
  phone: 'a phone number',
}

/**
 * What the active provider can actually sell, as a sentence fragment.
 *
 * The copy inviting someone to select contacts used to name all three fields.
 * Naming one the active provider has no endpoint for advertises a purchase that
 * is greyed out two clicks later.
 */
export function buyableFields(view: ProviderView): string {
  const phrases = (Object.keys(FIELD_PHRASE) as EnrichField[])
    .filter((field) => fieldUnavailableReason(field, view) === null)
    .map((field) => FIELD_PHRASE[field])
  if (phrases.length <= 1) return phrases[0] ?? 'what the active provider supports'
  return `${phrases.slice(0, -1).join(', ')} or ${phrases.at(-1)}`
}
